/* NIMO first-party measurements. No third-party pixels, PII or paid-click IDs.
 * Transport schema 1 is independent of measurement era 3.
 * Shopify consent is authoritative; absent/failed API means no collection. */
(() => {
  'use strict';
  if (window.NimoAnalytics) return;
  let config = {};
  try { config = JSON.parse(document.getElementById('nimo-config')?.textContent || '{}'); } catch { return; }
  const ERA = 3, SESSION_KEY = 'nimo_shopify_session_v3';
  const permittedEvents = new Set(['nimo_page_view', 'nimo_attribution', 'nimo_section_view',
    'nimo_section_engagement', 'nimo_scroll_depth', 'nimo_video_start', 'nimo_video_progress',
    'nimo_video_complete', 'nimo_cta_click', 'nimo_sku_selected', 'nimo_deposit_intent',
    'nimo_checkout_attempt', 'nimo_cart_added', 'nimo_cart_error', 'nimo_page_exit',
    'nimo_email_capture_accepted', 'nimo_rage_click', 'nimo_dead_click', 'nimo_content_copy',
    'nimo_tier_hesitation', 'nimo_scroll_rhythm', 'nimo_focus_return', 'nimo_exit_intent',
    'nimo_anchor_navigate', 'nimo_video_engagement', 'nimo_web_vital']);
  const textKeys = new Set(['section_id', 'current_section', 'exit_section', 'exit_reason',
    'video_id', 'cta_id', 'cta_location', 'selected_tier', 'selected_sku', 'sku_id', 'tier_id',
    'shopify_variant_id', 'checkout_transport', 'order_status', 'offer_phase', 'utm_source',
    'utm_medium', 'utm_campaign', 'utm_content', 'attr_campaign_id', 'attr_adset_id',
    'attr_ad_id', 'attr_placement', 'attr_channel', 'checkout_attempt_id', 'source_site', 'origin',
    'selection_length_bucket', 'from_anchor', 'to_anchor', 'metric_name', 'metric_rating', 'target_tag',
    'referrer_host', 'attr_latest_channel', 'attr_latest_ad_id', 'attr_latest_placement',
    'newsletter_provider', 'capture_status', 'form_id']);
  const numberKeys = new Set(['engagement_time_msec', 'visit_index', 'scroll_percent',
    'max_scroll_percent', 'video_percent', 'video_duration_seconds', 'video_watch_seconds',
    'elapsed_seconds', 'active_time_msec', 'click_count', 'hover_ms', 'away_ms', 'reversal_count',
    'watch_time_sec', 'furthest_percent', 'seek_count', 'pause_count', 'replay_count', 'metric_value', 'backtrack_ratio',
    'active_time_sec', 'elapsed_time_sec']);
  let session = '', page = '', sequence = 0, queue = [], timer = null, sending = false;
  let pageSent = false, exited = false, consentEpoch = 0;
  const sections = new Map(), videos = new Map(), depths = new Set(), newsletterSeen = new Set();
  const started = Date.now();
  const uuid = () => window.crypto.randomUUID();
  function allowed(purpose = 'analytics') {
    const privacy = window.Shopify?.customerPrivacy;
    try {
      if (navigator.globalPrivacyControl || navigator.doNotTrack === '1') return false;
      return purpose === 'marketing' ? privacy?.marketingAllowed() === true
        : privacy?.analyticsProcessingAllowed() === true;
    } catch { return false; }
  }
  function label(value) {
    const text = String(value ?? '').trim();
    if (!text || text.length > 128 || /[@{}\u0000-\u001f]|:\/\/|[?&=]|%40/i.test(text)) return '';
    return text.replace(/[^\p{L}\p{N}._~-]/gu, '_');
  }
  function sanitize(input) {
    const output = {};
    Object.entries(input || {}).forEach(([key, value]) => {
      if (textKeys.has(key)) { const safe = label(value); if (safe) output[key] = safe; }
      if (numberKeys.has(key) && typeof value === 'number' && Number.isFinite(value)) output[key] = Math.max(0, value);
      if (key === 'has_paid_click_id' && typeof value === 'boolean') output[key] = value;
      if (key === 'target_interactive' && typeof value === 'boolean') output[key] = value;
    });
    if (!allowed('marketing')) Object.keys(output).forEach(key => {
      if (key.startsWith('utm_') || key.startsWith('attr_') || key === 'has_paid_click_id') delete output[key];
    });
    return output;
  }
  function endpoint() {
    try {
      const url = new URL(config.analyticsEndpoint || '');
      // A typo or pasted third-party URL must not redirect anonymous business data.
      // A future collector migration requires a reviewed code change, not only a setting.
      const approvedProduction = 'https://nimo-v50-analytics-collector.nimo-v50-analytics-collector.workers.dev/v1/events';
      const approvedQa = 'https://nimo-v50-analytics-collector-qa.nimo-v50-analytics-collector.workers.dev/v1/events';
      const build = String(config.siteBuildId || '');
      const isQa = config.themeRole === 'unpublished' && build.startsWith('qa-');
      const approved = isQa ? approvedQa : approvedProduction;
      return url.href === approved ? approved : '';
    } catch { return ''; }
  }
  function productionContext() {
    // Preview query flags can disappear on /cart. Require the server-rendered
    // role too; absent/changed deprecated Liquid role fails closed, never to main.
    try {
      const build = String(config.siteBuildId || '');
      const preview = new URLSearchParams(window.location.search).has('preview_theme_id');
      const main = config.themeRole === 'main' && config.designMode === false &&
        config.visualPreviewMode === false && window.Shopify?.designMode !== true && !preview;
      const qa = config.qaMode === true && config.themeRole === 'unpublished' && build === 'shopify-theme-v3-qa' && preview &&
        config.designMode === false && config.visualPreviewMode !== true && window.Shopify?.designMode !== true;
      return main || qa;
    } catch { return false; }
  }
  function configured() {
    return config.analyticsEnabled === true && !!endpoint() && productionContext();
  }
  function redactMarketing(parameters) {
    const safe = {...parameters};
    if (!allowed('marketing')) Object.keys(safe).forEach(key => {
      if (key.startsWith('utm_') || key.startsWith('attr_') || key === 'has_paid_click_id') delete safe[key];
    });
    return safe;
  }
  function identity() {
    if (!configured() || !allowed()) return false;
    if (!session) {
      try {
        const stored = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
        session = stored && Date.now() - stored.at < 30 * 60 * 1000 && /^[a-f0-9-]{36}$/i.test(stored.id)
          ? stored.id : uuid();
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({id: session, at: Date.now()}));
      } catch { session = uuid(); }
    }
    if (!page) page = uuid();
    return true;
  }
  function emit(event, parameters = {}) {
    if (!permittedEvents.has(event) || !configured() || !allowed() || !identity()) return null;
    const envelope = {schema_version: 1, event, event_id: uuid(), occurred_at: new Date().toISOString(),
      session_id: session, page_view_id: page, sequence: ++sequence,
      parameters: {...sanitize(parameters), tracking_version: ERA, site_id: 'nimoar-shopify',
        site_build_id: label(config.siteBuildId || 'shopify-theme-v3'), release: 'shopify-v3',
        source_site: 'nimoar.com', origin: 'shopify_theme', experience: 'shopify_native',
        locale: label(document.documentElement.lang), direction: document.documentElement.dir || 'ltr',
        device_class: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1100 ? 'tablet' : 'desktop',
        viewport_width: window.innerWidth, viewport_height: window.innerHeight}};
    queue.push(envelope); queue = queue.slice(-100);
    if (!timer) timer = setTimeout(() => { timer = null; flush(); }, 1500);
    return envelope.event_id;
  }
  async function flush() {
    if (sending || !queue.length || !configured() || !allowed()) return false;
    sending = true;
    // Consent can change after enqueue. Re-check at actual transmission, not just emit.
    const batch = queue.slice(0, 20).map(item => ({...item, parameters:redactMarketing(item.parameters)})), epoch = consentEpoch;
    try {
      const response = await fetch(endpoint(), {method: 'POST', credentials: 'omit', mode: 'cors', redirect: 'error',
        headers: {'Content-Type': 'text/plain;charset=UTF-8'},
        body: JSON.stringify({events: batch}), keepalive: true});
      if (!response.ok) return false;
      if (epoch === consentEpoch) {
        const sent = new Set(batch.map(item => item.event_id));
        queue = queue.filter(item => !sent.has(item.event_id));
      }
      return true; // Transport acceptance only, never dashboard/platform delivery proof.
    } catch { return false; }
    finally {
      sending = false;
      if (queue.length && configured() && allowed() && !timer) timer = setTimeout(() => { timer = null; flush(); }, 10000);
    }
  }
  function consentChanged() {
    if (!allowed('marketing')) queue = queue.map(item => ({...item,parameters:redactMarketing(item.parameters)}));
    if (!configured() || !allowed()) {
      consentEpoch++; queue = []; session = ''; page = ''; pageSent = false;
      if (timer) clearTimeout(timer); timer = null;
      try { sessionStorage.removeItem(SESSION_KEY); } catch { /* denied storage */ }
      for (const state of sections.values()) { state.ms = 0; state.viewed = false; state.engaged = false; state.viewAt = 0; }
      depths.clear();
      newsletterSeen.clear();
      try {
        Object.keys(sessionStorage).filter(key => key.startsWith('nimo_newsletter_accept_v3:'))
          .forEach(key => sessionStorage.removeItem(key));
      } catch { /* optional storage */ }
      for (const state of videos.values()) { state.started = false; state.milestones.clear(); }
      return;
    }
    if (!pageSent) {
      let host='';try { host=label(new URL(document.referrer).hostname); } catch {}
      pageSent = !!emit('nimo_page_view', {referrer_host:host});
    }
  }
  function sectionTick() {
    const now = Date.now();
    for (const [node, state] of sections) {
      const delta = Math.min(1500, now - state.last); state.last = now;
      if (!configured() || !allowed() || document.visibilityState !== 'visible') { state.viewAt = 0; continue; }
      if (state.ratio >= 0.5) {
        if (!state.viewAt) state.viewAt = now;
        if (!state.viewed && now - state.viewAt >= 1000) {
          state.viewed = true; emit('nimo_section_view', {section_id: state.id, visit_index: state.visits});
        }
      } else state.viewAt = 0;
      if (state.ratio >= 0.25) {
        state.ms += delta;
        if (!state.engaged && state.ms >= 3000) {
          state.engaged = true; emit('nimo_section_engagement', {section_id: state.id,
            engagement_time_msec: Math.round(state.ms), visit_index: state.visits});
        }
      }
      if (!node.isConnected) sections.delete(node);
    }
  }
  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const state = sections.get(entry.target); if (!state) return;
      if (entry.intersectionRatio >= 0.25 && state.ratio < 0.25) state.visits++;
      state.ratio = entry.intersectionRatio;
    });
  }, {threshold: [0, 0.25, 0.5, 1]}) : null;
  function newsletterAccepted(provider, formId) {
    // An accepted form is not a confirmed subscriber, a unique contact or Meta Lead.
    // Dedup one provider/form per consented tab session; never inspect form values.
    const id = label(formId), key = `${provider}:${id}`;
    if (!id || newsletterSeen.has(key) || !configured() || !allowed()) return;
    const storageKey = `nimo_newsletter_accept_v3:${key}`;
    try {
      const previous = Number(sessionStorage.getItem(storageKey));
      if (previous > 0 && Date.now() - previous < 30 * 60 * 1000) return;
    } catch { /* memory-only dedup when storage is unavailable */ }
    if (emit('nimo_email_capture_accepted', {newsletter_provider: provider,
      capture_status: 'accepted_not_confirmed', form_id: id})) {
      newsletterSeen.add(key);
      try { sessionStorage.setItem(storageKey, String(Date.now())); } catch { /* optional storage */ }
    }
  }
  function mount(root = document) {
    root.querySelectorAll('[data-nimo-email-accepted="shopify"]').forEach(node => {
      const form = node.closest('form');
      if (form) newsletterAccepted('shopify', form.id);
    });
    root.querySelectorAll('[data-nimo-section]').forEach(node => {
      if (sections.has(node)) return;
      sections.set(node, {id: label(node.dataset.nimoSection), ratio: 0, last: Date.now(), ms: 0,
        visits: 0, viewAt: 0, viewed: false, engaged: false});
      observer?.observe(node);
    });
    root.querySelectorAll('video').forEach(video => {
      if (videos.has(video)) return;
      const state = {started: false, milestones: new Set(),
        id: label(video.dataset.nimoVideo || video.closest('[data-nimo-section]')?.dataset.nimoSection || 'video')};
      const play = () => { if (!state.started && emit('nimo_video_start', {video_id: state.id})) state.started = true; };
      const progress = () => {
        if (!Number.isFinite(video.duration) || video.duration <= 0) return;
        for (const percent of [25, 50, 75, 90]) {
          if (!state.milestones.has(percent) && video.currentTime / video.duration * 100 >= percent &&
              emit('nimo_video_progress', {video_id: state.id, video_percent: percent,
                video_duration_seconds: video.duration})) state.milestones.add(percent);
        }
      };
      const complete = () => emit('nimo_video_complete', {video_id: state.id, video_percent: 100});
      Object.assign(state, {play, progress, complete}); videos.set(video, state);
      video.addEventListener('play', play); video.addEventListener('timeupdate', progress); video.addEventListener('ended', complete);
    });
  }
  function unmount(root) {
    for (const [node] of sections) if (root.contains(node)) { observer?.unobserve(node); sections.delete(node); }
    for (const [video, state] of videos) if (root.contains(video)) {
      video.removeEventListener('play', state.play); video.removeEventListener('timeupdate', state.progress);
      video.removeEventListener('ended', state.complete); videos.delete(video);
    }
  }
  function scroll() {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    if (max <= 0) return;
    const reached = Math.min(100, (window.scrollY + 2) / max * 100);
    for (const percent of [25, 50, 75, 90, 100]) if (reached >= percent && !depths.has(percent) &&
      emit('nimo_scroll_depth', {scroll_percent: percent})) depths.add(percent);
  }
  function exit() {
    if (exited) return; exited = true;
    let current = ''; for (const state of sections.values()) if (state.ratio > 0.25) current = state.id;
    const exitDetails={};document.dispatchEvent(new CustomEvent('nimo:before-exit',{detail:exitDetails}));
    emit('nimo_page_exit', {...exitDetails, exit_section: current, exit_reason: 'pagehide_proxy',
      elapsed_time_sec:(Date.now()-started)/1000,elapsed_seconds:(Date.now()-started)/1000});
    flush(); // pagehide is a proxy, not guaranteed browser closure detection.
  }
  window.NimoAnalytics = Object.freeze({emit, flush, allowed, label, sanitize, configured, trackingVersion: ERA});
  document.addEventListener('visitorConsentCollected', consentChanged);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => unmount(event.target));
  document.addEventListener('click', event => {
    const node = event.target.closest?.('[data-nimo-cta]');
    if (node) emit('nimo_cta_click', {cta_id: node.dataset.nimoCta,
      cta_location: node.closest('[data-nimo-section]')?.dataset.nimoSection});
  });
  window.addEventListener('scroll', scroll, {passive: true});
  window.addEventListener('klaviyoForms', event => {
    const configured = label(config.klaviyoFormId);
    if (configured && event.detail?.type === 'submit' && event.detail.formId === configured)
      newsletterAccepted('klaviyo', `nimo-klaviyo-${configured}`);
  });
  window.addEventListener('pagehide', exit);
  window.addEventListener('pageshow', event => { if (event.persisted) exited = false; });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });
  mount(); setInterval(sectionTick, 1000);
  consentChanged();
  if (window.Shopify?.loadFeatures) window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], error => {
    if (!error) { consentChanged(); document.dispatchEvent(new CustomEvent('nimo:privacy-ready')); }
  });
})();
